import type { PropsWithChildren } from 'react'

import { useFaceGroupingConsentInfoQuery } from '@jotta/grpc-connect-client/people/faceGroupingConsent'
import { lazyWithRetry } from '@jotta/ui/lazyWithRetry'

import { AlbumSectionContainer } from '../AlbumsSection/AlbumSectionContainer'

const LazyFaceGroupingConsentSection = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "FaceGroupingConsentSection" */ '../../FaceGroupingConsent/FaceGroupingConsentSection'
  ).then(module => ({
    default: module.FaceGroupingConsentSection,
  })),
)

const LazyPeopleSectionContainer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PeopleSectionContainer" */
    '../People/PeopleSectionContainer'
  ).then(module => ({
    default: module.PeopleSectionContainer,
  })),
)

export function AlbumListFrame({ children }: PropsWithChildren) {
  return (
    <div className="relative">
      <div className="flex w-full flex-col gap-3 overflow-hidden px-2 pb-16 pt-4 opacity-[var(--content-opacity,1)] transition-opacity duration-300 sm:px-3 lg:px-4">
        {children}
      </div>
    </div>
  )
}

export function AlbumList() {
  const { data: faceGroupingConsentInfo } = useFaceGroupingConsentInfoQuery()
  const isFaceGroupingEnabled =
    faceGroupingConsentInfo?.isFaceGroupsEnabled === true
  const hasFaceGroupingConsent = faceGroupingConsentInfo?.hasConsent === true
  const faceGroupingGrantKind = faceGroupingConsentInfo?.grant.kind
  const faceGroupingPrompt =
    faceGroupingGrantKind === 'primaryPrompt' ||
    faceGroupingGrantKind === 'secondaryPrompt'
      ? faceGroupingGrantKind
      : undefined

  // The index route owns its surface (see its route handle), so the consent
  // card sits beside the albums card rather than inside it.
  return (
    <div className="flex grow flex-col lg:gap-3">
      {isFaceGroupingEnabled && faceGroupingPrompt && (
        <LazyFaceGroupingConsentSection prompt={faceGroupingPrompt} />
      )}
      <div className="grow bg-surface-container-lowest lg:rounded-xl">
        <AlbumListFrame>
          {isFaceGroupingEnabled && hasFaceGroupingConsent && (
            <LazyPeopleSectionContainer />
          )}
          <AlbumSectionContainer albumType="NORMAL_ALBUM" />
          <AlbumSectionContainer albumType="META_SHARED_ALBUM_TYPES" />
          <AlbumSectionContainer albumType="LOCATION_ALBUM" />
        </AlbumListFrame>
      </div>
    </div>
  )
}
